import { useState } from 'react';
import { Link } from 'react-router-dom';
import { nomoStories, storyCategories, type StoryCategory } from '@/mocks/stories';

type Filter = 'ALL' | StoryCategory;

const chipTone: Record<StoryCategory, string> = {
  FOOD: 'bg-primary-100 text-primary-700',
  PLACE: 'bg-secondary-100 text-secondary-700',
  PEOPLE: 'bg-accent-100 text-accent-700',
  CHARACTER: 'bg-primary-100 text-primary-700',
  PLAY: 'bg-secondary-100 text-secondary-700',
};

export default function Stories() {
  const [filter, setFilter] = useState<Filter>('ALL');

  const list = filter === 'ALL' ? nomoStories : nomoStories.filter((s) => s.category === filter);
  const isSingle = list.length === 1;
  const activeCategory = storyCategories.find((c) => c.key === filter);

  return (
    <section id="stories" className="bg-background-100 py-16 md:py-28">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="reveal mb-10 md:mb-14">
          <div className="mb-6 flex items-center gap-3">
            <span className="text-[11px] tracking-[0.34em] text-primary-600 uppercase">
              Section 06
            </span>
            <span className="h-px w-8 bg-primary-300"></span>
            <span className="text-[11px] tracking-[0.28em] text-foreground-400 uppercase">
              NOMOISM Stories
            </span>
          </div>
          <h2 className="font-heading text-3xl leading-snug text-foreground-950 md:text-5xl">
            每一个角色，都有自己的故事
          </h2>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里不只是品牌新闻，而是一座内容型品牌资产库：从一道菜，到一个地方，到一群人，最后到一个角色。
          </p>
        </div>

        <div className="reveal mb-8 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setFilter('ALL')}
            className={[
              'rounded-full px-4 py-2 text-xs tracking-[0.08em] transition-colors duration-300 cursor-pointer whitespace-nowrap',
              filter === 'ALL'
                ? 'bg-foreground-950 text-background-50'
                : 'bg-background-50 text-foreground-700 hover:text-primary-600',
            ].join(' ')}
          >
            全部故事
          </button>
          {storyCategories.map((category) => (
            <button
              key={category.key}
              type="button"
              onClick={() => setFilter(category.key)}
              className={[
                'rounded-full px-4 py-2 text-xs tracking-[0.08em] transition-colors duration-300 cursor-pointer whitespace-nowrap',
                filter === category.key
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-50 text-foreground-700 hover:text-primary-600',
              ].join(' ')}
            >
              {category.zh}
            </button>
          ))}
        </div>

        {activeCategory && (
          <p className="reveal mb-6 text-sm text-foreground-500">
            {activeCategory.zh} — {activeCategory.label}
          </p>
        )}

        <div
          className={`grid grid-cols-1 gap-5 ${
            isSingle ? '' : 'md:grid-cols-2 lg:grid-cols-3'
          }`}
        >
          {list.map((story, index) => (
            <article
              key={story.id}
              className={`reveal group overflow-hidden rounded-lg border border-background-200/70 bg-background-50 ${
                isSingle ? 'md:grid md:grid-cols-2' : ''
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div
                className={`relative w-full overflow-hidden bg-background-100 ${
                  isSingle ? 'h-[240px] md:h-full md:min-h-[320px]' : 'h-[220px]'
                }`}
              >
                <img
                  src={story.image}
                  alt={`NOMOISM 品牌故事：${story.title}`}
                  title={`NOMOISM ${story.categoryZh} · ${story.title}`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.05]"
                />
                <span
                  className={`absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] tracking-[0.14em] ${chipTone[story.category]}`}
                >
                  {story.category}
                </span>
              </div>

              <div className={`flex flex-col justify-center p-6 ${isSingle ? 'md:p-9' : ''}`}>
                <p className="text-[11px] tracking-[0.2em] text-foreground-400 uppercase">
                  {story.categoryZh} · {story.date}
                </p>
                <h3
                  className={`mt-3 font-heading leading-snug text-foreground-950 ${
                    isSingle ? 'text-2xl md:text-3xl' : 'text-lg'
                  }`}
                >
                  {story.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{story.excerpt}</p>
                <Link
                  to={`/stories/${story.id}`}
                  className="mt-5 inline-flex items-center gap-2 text-sm text-primary-600"
                >
                  继续阅读
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                </Link>
              </div>
            </article>
          ))}
        </div>

        <div className="reveal mt-10 text-center">
          <Link
            to="/stories"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-primary-300 px-6 py-3 text-sm text-primary-600 transition-colors duration-300 hover:bg-primary-500 hover:text-background-50 cursor-pointer whitespace-nowrap"
          >
            查看全部故事
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}